import './parceiro-form-modal.js';
import '../comissoes/pagar-comissao-modal.js';
import type { Parceiro, Comissao } from '@pgcustom/shared-types';
import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Resultado } from '../../core/resultado.js';
import type { PgTable, PgTableColumn, PgTableRow, PgChart, PgKpiCard } from '@pgcustom/design-system';
import type { ParceirosApi } from './parceiros.api.js';
import type { PermissoesParceiros } from './parceiros.permissoes.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { PermissoesComissoes } from '../comissoes/comissoes.permissoes.js';
import type { FinanceiroApi } from '../financeiro/financeiro.api.js';
import { formatarDataHora } from '../pedidos/formatacao.js';
import type { ErpParceiroFormModal } from './parceiro-form-modal.js';
import type { ErpPagarComissaoModal } from '../comissoes/pagar-comissao-modal.js';

const COLUNAS_PARCEIROS: PgTableColumn[] = [
  { key: 'nome', label: 'Nome' },
  { key: 'email', label: 'E-mail' },
  { key: 'ativo', label: 'Status' },
];

const COLUNAS_COMISSOES: PgTableColumn[] = [
  { key: 'tipo', label: 'Tipo' },
  { key: 'beneficiario', label: 'Beneficiário' },
  { key: 'valor', label: 'Valor', align: 'right' },
  { key: 'status', label: 'Status' },
  { key: 'criadoEm', label: 'Gerada em' },
];

/**
 * `<erp-parceiros-page>` — cadastro de Parceiros (Sprint 7, cadastro
 * rico + cupons) e Comissões (parceiro/técnico, ciclo de vida
 * pendente→paga/estornada — `PRODUCT.md`, 3.6). Mesmo padrão de
 * `<erp-financeiro-page>`: tabela + modal de formulário, dado sempre
 * real. Portal do parceiro (login próprio) fora de escopo — DEC-029.
 */
export class ErpParceirosPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _parceirosApi: ParceirosApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _financeiroApi: FinanceiroApi | null = null;
  private _permissoesParceiros: PermissoesParceiros = { ler: false, criar: false, atualizar: false };
  private _permissoesComissoes: PermissoesComissoes = { ler: false, atualizar: false };

  private resultadoParceiros: Resultado<Parceiro[]> = { status: 'carregando' };
  private resultadoComissoes: Resultado<Comissao[]> = { status: 'carregando' };
  private parceirosAtuais: Parceiro[] = [];
  private comissoesAtuais: Comissao[] = [];
  private mapaParceiros = new Map<string, string>();

  private modalParceiro!: ErpParceiroFormModal;
  private modalPagar!: ErpPagarComissaoModal;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set parceirosApi(valor: ParceirosApi) {
    this._parceirosApi = valor;
    if (this.isConnected) void this.carregar();
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
    if (this.isConnected) void this.carregarComissoes();
  }

  set financeiroApi(valor: FinanceiroApi) {
    this._financeiroApi = valor;
  }

  set permissoesParceiros(valor: PermissoesParceiros) {
    this._permissoesParceiros = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  set permissoesComissoes(valor: PermissoesComissoes) {
    this._permissoesComissoes = valor;
    if (this.isConnected) this.renderEsqueleto();
  }

  connectedCallback(): void {
    this.renderEsqueleto();
    if (this._parceirosApi) void this.carregar();
    if (this._comissoesApi) void this.carregarComissoes();
  }

  private renderEsqueleto(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        .topo {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
          flex-wrap: wrap;
        }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0;
        }
        .subtitulo { font-size: var(--pg-font-size-sm, 0.875rem); color: var(--pg-color-texto-secundario, #9aa1ab); margin-top: 2px; }
        h2 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-md, 1.125rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: var(--pg-space-6, 32px) 0 var(--pg-space-4, 16px);
        }
        .kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: var(--pg-space-4, 16px); margin-bottom: var(--pg-space-4, 16px); }
        .layout { display: grid; grid-template-columns: minmax(0, 1fr) minmax(220px, 280px); gap: var(--pg-space-4, 16px); align-items: start; }
        @media (max-width: 900px) { .layout { grid-template-columns: 1fr; } }
      </style>
      <div class="topo">
        <div>
          <h1>Parcerias</h1>
          <div class="subtitulo">Parceiros e comissões — pendente, paga e estornada</div>
        </div>
        ${this._permissoesParceiros.criar ? '<pg-button id="botao-novo" variant="primary">Novo parceiro</pg-button>' : ''}
      </div>
      <pg-card padding="compacto">
        <div id="parceiros"></div>
      </pg-card>

      <h2>Comissões</h2>
      <div class="kpis">
        <pg-kpi-card id="kpi-pendente" label="Pendente" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-paga" label="Paga" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-estornada" label="Estornada" icone="dinheiro" estado="carregando"></pg-kpi-card>
      </div>
      <div class="layout">
        <pg-card padding="compacto">
          <div id="comissoes"></div>
        </pg-card>
        <pg-card padding="compacto">
          <span slot="header">Comissões por status</span>
          <div id="donut-comissoes"><pg-empty-state variant="carregando" titulo="Comissões por status"></pg-empty-state></div>
        </pg-card>
      </div>

      <erp-parceiro-form-modal id="modal-parceiro"></erp-parceiro-form-modal>
      <erp-pagar-comissao-modal id="modal-pagar"></erp-pagar-comissao-modal>
    `;

    this.modalParceiro = this.root.querySelector('#modal-parceiro') as ErpParceiroFormModal;
    if (this._parceirosApi) this.modalParceiro.parceirosApi = this._parceirosApi;
    this.modalParceiro.permissoes = this._permissoesParceiros;
    this.modalParceiro.aoSalvar = () => void this.carregar();

    this.modalPagar = this.root.querySelector('#modal-pagar') as ErpPagarComissaoModal;
    if (this._comissoesApi) this.modalPagar.comissoesApi = this._comissoesApi;
    this.modalPagar.aoPagar = () => void this.carregarComissoes();

    this.root.querySelector('#botao-novo')?.addEventListener('pg-click', () => {
      this.modalParceiro.abrirParaCriar();
    });

    this.root.querySelector('#parceiros')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const parceiro = this.parceirosAtuais[index];
      if (parceiro) this.modalParceiro.abrirParaEditar(parceiro);
    });

    this.root.querySelector('#comissoes')?.addEventListener('pg-row-click', (evento) => {
      const { index } = (evento as CustomEvent<{ row: PgTableRow; index: number }>).detail;
      const comissao = this.comissoesAtuais[index];
      if (comissao && comissao.status === 'pendente' && this._permissoesComissoes.atualizar) {
        void this.abrirPagamento(comissao);
      }
    });

    this.atualizarParceiros();
    this.atualizarComissoes();
  }

  private async abrirPagamento(comissao: Comissao): Promise<void> {
    if (!this._financeiroApi) return;
    const resultado = await this._financeiroApi.listarContas();
    const contas = resultado.status === 'ok' ? resultado.dado : [];
    this.modalPagar.abrir(comissao, contas);
  }

  private async carregar(): Promise<void> {
    const api = this._parceirosApi;
    if (!api) return;

    if (!this._permissoesParceiros.ler) {
      this.resultadoParceiros = { status: 'sem-permissao' };
      this.atualizarParceiros();
      return;
    }

    this.resultadoParceiros = { status: 'carregando' };
    this.atualizarParceiros();
    this.resultadoParceiros = await api.listar();
    if (this.resultadoParceiros.status === 'ok') {
      for (const p of this.resultadoParceiros.dado) this.mapaParceiros.set(p.id, p.nome);
    }
    this.atualizarParceiros();
  }

  private async carregarComissoes(): Promise<void> {
    const api = this._comissoesApi;
    if (!api) return;

    if (!this._permissoesComissoes.ler) {
      this.resultadoComissoes = { status: 'sem-permissao' };
      this.atualizarComissoes();
      return;
    }

    this.resultadoComissoes = { status: 'carregando' };
    this.atualizarComissoes();
    this.resultadoComissoes = await api.listar();
    this.atualizarComissoes();
  }

  private atualizarParceiros(): void {
    const container = this.root.querySelector('#parceiros');
    if (!container) return;

    if (this.resultadoParceiros.status === 'ok') {
      this.parceirosAtuais = this.resultadoParceiros.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS_PARCEIROS;
      tabela.rows = this.parceirosAtuais.map((p) => ({
        nome: p.nome,
        email: p.email ?? '—',
        ativo: p.ativo ? { texto: 'Ativo', tom: 'sucesso' as const } : { texto: 'Inativo', tom: 'neutro' as const },
      }));
      return;
    }

    this.parceirosAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoParceiros, 'Parceiros', 'nenhum parceiro cadastrado ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarComissoes(): void {
    const container = this.root.querySelector('#comissoes');
    if (!container) return;

    if (this.resultadoComissoes.status === 'ok') {
      this.comissoesAtuais = this.resultadoComissoes.dado;
      container.innerHTML = '<pg-table></pg-table>';
      const tabela = container.querySelector('pg-table') as PgTable;
      tabela.columns = COLUNAS_COMISSOES;
      tabela.rows = this.comissoesAtuais.map(comissaoParaLinha.bind(null, this.mapaParceiros));
      this.atualizarResumoComissoes(this.comissoesAtuais);
      return;
    }

    this.comissoesAtuais = [];
    const { variant, titulo, descricao } = estadoVazio(this.resultadoComissoes, 'Comissões', 'nenhuma comissão gerada ainda');
    container.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
    for (const id of ['kpi-pendente', 'kpi-paga', 'kpi-estornada']) {
      const kpi = this.root.querySelector(`#${id}`) as PgKpiCard | null;
      if (!kpi) continue;
      if (this.resultadoComissoes.status === 'vazio') {
        kpi.setAttribute('estado', 'ok');
        kpi.setAttribute('valor', formatBRL(0));
      } else {
        kpi.setAttribute('estado', variant === 'carregando' ? 'carregando' : 'indisponivel');
      }
    }
    const donut = this.root.querySelector('#donut-comissoes');
    if (donut) donut.innerHTML = `<pg-empty-state variant="${variant}" titulo="Comissões por status" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private atualizarResumoComissoes(comissoes: Comissao[]): void {
    const somaPor = (status: Comissao['status']) =>
      comissoes.filter((c) => c.status === status).reduce((soma, c) => soma + c.valorCentavos, 0);
    const pendente = somaPor('pendente');
    const paga = somaPor('paga');
    const estornada = somaPor('estornada');

    (this.root.querySelector('#kpi-pendente') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-pendente') as PgKpiCard | null)?.setAttribute('valor', formatBRL(centavosParaReais(pendente)));
    (this.root.querySelector('#kpi-paga') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-paga') as PgKpiCard | null)?.setAttribute('valor', formatBRL(centavosParaReais(paga)));
    (this.root.querySelector('#kpi-estornada') as PgKpiCard | null)?.setAttribute('estado', 'ok');
    (this.root.querySelector('#kpi-estornada') as PgKpiCard | null)?.setAttribute('valor', formatBRL(centavosParaReais(estornada)));

    const donut = this.root.querySelector('#donut-comissoes');
    if (!donut) return;
    donut.innerHTML = '<pg-chart></pg-chart>';
    const chart = donut.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.points = [
      { label: 'Pendente', values: { valor: pendente }, color: 'var(--pg-color-alerta)' },
      { label: 'Paga', values: { valor: paga }, color: 'var(--pg-color-sucesso)' },
      { label: 'Estornada', values: { valor: estornada }, color: 'var(--pg-color-texto-secundario)' },
    ];
    chart.formatarValor = (centavos) => formatBRL(centavosParaReais(centavos));
  }
}

const TOM_STATUS_COMISSAO: Record<Comissao['status'], 'alerta' | 'sucesso' | 'neutro'> = {
  pendente: 'alerta',
  paga: 'sucesso',
  estornada: 'neutro',
};

function comissaoParaLinha(mapaParceiros: Map<string, string>, comissao: Comissao) {
  const beneficiario =
    comissao.tipo === 'parceiro' ? (mapaParceiros.get(comissao.beneficiarioId) ?? comissao.beneficiarioId) : comissao.beneficiarioId;
  return {
    tipo: comissao.tipo === 'parceiro' ? 'Parceiro' : 'Técnico',
    beneficiario,
    valor: formatBRL(centavosParaReais(comissao.valorCentavos)),
    status: {
      texto: comissao.status === 'pendente' ? 'Pendente' : comissao.status === 'paga' ? 'Paga' : 'Estornada',
      tom: TOM_STATUS_COMISSAO[comissao.status],
    },
    criadoEm: formatarDataHora(comissao.criadoEm),
  };
}

function estadoVazio<T>(resultado: Resultado<T>, titulo: string, mensagemVazio: string): { variant: string; titulo: string; descricao: string } {
  switch (resultado.status) {
    case 'carregando':
      return { variant: 'carregando', titulo, descricao: '' };
    case 'sem-permissao':
      return { variant: 'sem-permissao', titulo, descricao: 'você não tem permissão para ver este dado' };
    case 'erro':
      return { variant: 'erro', titulo, descricao: resultado.mensagem };
    default:
      return { variant: 'vazio', titulo: `Nenhum registro`, descricao: mensagemVazio };
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-parceiros-page', ErpParceirosPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-parceiros-page': ErpParceirosPage;
  }
}
